<template>
  <div
    class="asc-customAlerts-histogram-wrapper--container"
    data-automation-id="wrapper_customAlerts_histogram"
  >
    <div class="asc-customAlerts-histogram-wrapper--chartContainer">
      <asc-histogram
        :loading="loading"
        :unit-name="unitName"
        :parameter-name="parameterName"
        :airline-name="airlineName"
        :data="data"
        :alert-type="alertType"
        :min-range="minRange"
        :max-range="maxRange"
        :low-warning="lowWarning"
        :low-caution="lowCaution"
        :high-caution="highCaution"
        :high-warning="highWarning"
        :low-warning-valid="!isLowWarningInvalid"
        :low-caution-valid="!isLowCautionInvalid"
        :high-caution-valid="!isHighCautionInvalid"
        :high-warning-valid="!isHighWarningInvalid"
        :max-range-valid="!isMaxRangeInvalid"
      >
      </asc-histogram>
      <asc-parameter-threshold-table
        v-if="!data.error"
        :data="
          alertType === 'acmf'
            ? getSnapshotThresholdTableValue
            : getFullFlightThresholdTableValues
        "
      ></asc-parameter-threshold-table>
    </div>
    <asc-custom-alerts-threshold-wrapper
      v-if="!loading"
      :min-range="minRange"
      :max-range="maxRange"
      :high-caution="highCaution"
      :high-warning="highWarning"
      :low-warning="lowWarning"
      :low-caution="lowCaution"
      :is-range-min-enabled="isRangeMinEnabled"
      :is-range-max-enabled="isRangeMaxEnabled"
      :is-low-warning-enabled="isLowWarningEnabled"
      :is-high-warning-enabled="isHighWarningEnabled"
      :is-low-caution-enabled="isLowCautionEnabled"
      :is-high-caution-enabled="isHighCautionEnabled"
      :is-min-range-invalid="isMinRangeInvalid"
      :is-max-range-invalid="isMaxRangeInvalid"
      :is-low-warning-invalid="isLowWarningInvalid"
      :is-low-caution-invalid="isLowCautionInvalid"
      :is-high-caution-invalid="isHighCautionInvalid"
      :is-high-warning-invalid="isHighWarningInvalid"
      :is-threshold-alert-active="isThresholdAlertActive"
      @updateThresholdAlertStatus="handleUpdateThresholdAlertStatus"
      @updateMinRange="handleUpdateMinRange"
      @updateMaxRange="handleUpdateMaxRange"
      @updateLowWarning="handleUpdateLowWarning"
      @updateLowCaution="handleUpdateLowCaution"
      @updateHighCaution="handleUpdateHighCaution"
      @updateHighWarning="handleUpdateHighWarning"
    ></asc-custom-alerts-threshold-wrapper>
  </div>
</template>

<script>
import { AscCustomAlertMixin, AscHistogramMixin } from '@/components/mixins'
import AscHistogram from '@/components/molecules/AscHistogram'
import AscParameterThresholdTable from '@/components/molecules/AscParameterThresholdTable'
import AscCustomAlertsThresholdWrapper from '@/components/organisms/AscCustomAlertsThresholdWrapper'

export default {
  name: 'AscCustomAlertsHistogramWrapper',

  components: {
    AscParameterThresholdTable,
    AscHistogram,
    AscCustomAlertsThresholdWrapper,
  },

  mixins: [AscHistogramMixin, AscCustomAlertMixin],

  props: {
    parameterName: {
      type: String,
      default: '',
    },
    unitName: {
      type: String,
      default: '',
    },
    data: {
      type: Array,
      default: () => [],
    },
    xAxisTicks: {
      type: Array,
      default: () => [],
      required: true,
    },
    minRange: {
      type: [Number, String],
      default: null,
    },
    maxRange: {
      type: [Number, String],
      default: null,
    },
    lowWarning: {
      type: [Number, String],
      default: null,
    },
    lowCaution: {
      type: [Number, String],
      default: null,
    },
    highCaution: {
      type: [Number, String],
      default: null,
    },
    highWarning: {
      type: [Number, String],
      default: null,
    },
    histogramMaxValue: {
      type: Number,
      default: null,
    },
    histogramMinValue: {
      type: Number,
      default: null,
    },
    isRangeMinEnabled: {
      type: Boolean,
    },
    isRangeMaxEnabled: {
      type: Boolean,
    },
    isLowWarningEnabled: {
      type: Boolean,
    },
    isHighWarningEnabled: {
      type: Boolean,
    },
    isLowCautionEnabled: {
      type: Boolean,
    },
    isHighCautionEnabled: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    airlineName: {
      type: String,
      default: undefined,
    },
    alertType: {
      type: String,
      default: undefined,
    },
    isThresholdAlertActive: {
      type: Boolean,
    },
  },

  data() {
    return {}
  },

  computed: {
    getFullFlightThresholdTableValues() {
      let total = 0
      this.data.forEach(bucketObj => {
        total += bucketObj.flightCount
      })
      let normal = total
      let belowMinRange = null
      if (this.minRange !== null) {
        this.data.forEach(bucketObj => {
          if (bucketObj.value < this.minRange) {
            belowMinRange += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      }
      let aboveMaxRange = null
      if (this.maxRange !== null) {
        this.data.forEach(bucketObj => {
          if (bucketObj.value >= this.maxRange) {
            aboveMaxRange += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      }
      let lowWarning = null
      if (this.lowWarning !== null && this.minRange !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.value > this.minRange &&
            bucketObj.value < this.lowWarning
          ) {
            // if the threshold falls completely in the bucket take the full bucket count
            lowWarning += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      } else {
        if (this.lowWarning !== null) {
          this.data.forEach(bucketObj => {
            if (bucketObj.value < this.lowWarning) {
              // if the threshold falls completely in the bucket take the full bucket count
              lowWarning += bucketObj.flightCount
              normal -= bucketObj.flightCount
            }
          })
        }
      }
      let highWarning = null
      if (this.highWarning !== null && this.maxRange !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.value > this.highWarning &&
            bucketObj.value < this.maxRange
          ) {
            highWarning += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      } else {
        if (this.highWarning !== null) {
          this.data.forEach(bucketObj => {
            if (bucketObj.value > this.highWarning) {
              highWarning += bucketObj.flightCount
              normal -= bucketObj.flightCount
            }
          })
        }
      }
      let lowCaution = null
      if (this.lowCaution !== null && this.lowWarning !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.value > this.lowWarning &&
            bucketObj.value < this.lowCaution
          ) {
            lowCaution += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      } else {
        if (this.lowCaution !== null) {
          this.data.forEach(bucketObj => {
            if (bucketObj.value < this.lowCaution) {
              lowCaution += bucketObj.flightCount
              normal -= bucketObj.flightCount
            }
          })
        }
      }
      let highCaution = null
      if (this.highCaution !== null && this.highWarning !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.value > this.highCaution &&
            bucketObj.value < this.highWarning
          ) {
            highCaution += bucketObj.flightCount
            normal -= bucketObj.flightCount
          }
        })
      } else {
        if (this.highCaution !== null) {
          this.data.forEach(bucketObj => {
            if (bucketObj.value > this.highCaution) {
              highCaution += bucketObj.flightCount
              normal -= bucketObj.flightCount
            }
          })
        }
      }
      return {
        minRange: belowMinRange,
        maxRange: aboveMaxRange,
        lowWarning: lowWarning,
        lowCaution: lowCaution,
        highCaution: highCaution,
        highWarning: highWarning,
        normal: normal,
        total: total,
      }
    },
    getSnapshotThresholdTableValue() {
      let total = 0
      this.data.forEach(bucketObj => {
        total += bucketObj.bucketCount
      })
      let normal = total
      let belowMinRange = null
      if (this.minRange !== null) {
        this.data.forEach(bucketObj => {
          // if the threshold falls completely in the bucket (both min and max are less than the threshold), take the full bucket count
          if (bucketObj.min < this.minRange && bucketObj.max < this.minRange) {
            belowMinRange += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            // if the min is below the threshold, but the max is above it, take half the bucket count
            bucketObj.min < this.minRange &&
            bucketObj.max > this.minRange
          ) {
            belowMinRange += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      }
      let aboveMaxRange = null
      if (this.maxRange !== null) {
        this.data.forEach(bucketObj => {
          // if the threshold falls completely in the bucket, take the full bucket count
          if (bucketObj.min >= this.maxRange) {
            aboveMaxRange += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            // if the min is below the threshold, but the max is above it, take half the bucket count
            bucketObj.min <= this.maxRange &&
            bucketObj.max > this.maxRange
          ) {
            aboveMaxRange += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      }
      let lowWarning = null
      if (this.lowWarning && this.minRange) {
        this.data.forEach(bucketObj => {
          // if the threshold falls completely in the bucket, take the full bucket count
          if (
            bucketObj.min > this.minRange &&
            bucketObj.max < this.lowWarning
          ) {
            lowWarning += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            // if the min is below the threshold, but the max is above it, take half the bucket count
            bucketObj.min < this.lowWarning &&
            bucketObj.max >= this.lowWarning
          ) {
            lowWarning += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      } else {
        if (this.lowWarning) {
          this.data.forEach(bucketObj => {
            // if the threshold falls completely in the bucket, take the full bucket count
            if (
              bucketObj.min < this.lowWarning &&
              bucketObj.max < this.lowWarning
            ) {
              lowWarning += bucketObj.bucketCount
              normal -= bucketObj.bucketCount
            } else if (
              // if the min is below the threshold, but the max is above it, take half the bucket count
              bucketObj.min < this.lowWarning &&
              bucketObj.max >= this.lowWarning
            ) {
              lowWarning += bucketObj.bucketCount / 2
              normal -= bucketObj.bucketCount
            }
          })
        }
      }
      let highWarning = null
      if (this.highWarning !== null && this.maxRange !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.min > this.highWarning &&
            bucketObj.max < this.maxRange
          ) {
            highWarning += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            bucketObj.min < this.highWarning &&
            bucketObj.max >= this.highWarning
          ) {
            highWarning += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      } else {
        if (this.highWarning) {
          this.data.forEach(bucketObj => {
            if (
              bucketObj.min > this.highWarning &&
              bucketObj.max > this.highWarning
            ) {
              highWarning += bucketObj.bucketCount
              normal -= bucketObj.bucketCount
            } else if (
              bucketObj.min < this.highWarning &&
              bucketObj.max >= this.highWarning
            ) {
              highWarning += bucketObj.bucketCount / 2
              normal -= bucketObj.bucketCount
            }
          })
        }
      }
      let lowCaution = null
      if (this.lowCaution !== null && this.lowWarning !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.min > this.lowWarning &&
            bucketObj.max < this.lowCaution
          ) {
            lowCaution += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            // if the min is below the threshold, but the max is above it, take half the bucket count
            bucketObj.min < this.lowCaution &&
            bucketObj.max >= this.lowCaution
          ) {
            lowCaution += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      } else {
        if (this.lowCaution) {
          this.data.forEach(bucketObj => {
            if (
              bucketObj.min < this.lowCaution &&
              bucketObj.max < this.lowCaution
            ) {
              lowCaution += bucketObj.bucketCount
              normal -= bucketObj.bucketCount
            } else if (
              // if the min is below the threshold, but the max is above it, take half the bucket count
              bucketObj.min < this.lowCaution &&
              bucketObj.max >= this.lowCaution
            ) {
              lowCaution += bucketObj.bucketCount / 2
              normal -= bucketObj.bucketCount
            }
          })
        }
      }
      let highCaution = null
      if (this.highCaution !== null && this.highWarning !== null) {
        this.data.forEach(bucketObj => {
          if (
            bucketObj.min > this.highCaution &&
            bucketObj.max < this.highWarning
          ) {
            highCaution += bucketObj.bucketCount
            normal -= bucketObj.bucketCount
          } else if (
            bucketObj.min < this.highCaution &&
            bucketObj.max >= this.highCaution
          ) {
            highCaution += bucketObj.bucketCount / 2
            normal -= bucketObj.bucketCount
          }
        })
      } else {
        if (this.highCaution) {
          this.data.forEach(bucketObj => {
            if (
              bucketObj.min > this.highCaution &&
              bucketObj.max > this.highCaution
            ) {
              highCaution += bucketObj.bucketCount
              normal -= bucketObj.bucketCount
            } else if (
              bucketObj.min < this.highCaution &&
              bucketObj.max >= this.highCaution
            ) {
              highCaution += bucketObj.bucketCount / 2
              normal -= bucketObj.bucketCount
            }
          })
        }
      }
      return {
        minRange: belowMinRange,
        maxRange: aboveMaxRange,
        lowWarning: lowWarning,
        lowCaution: lowCaution,
        highCaution: highCaution,
        highWarning: highWarning,
        normal: normal,
        total: total,
      }
    },
    getHistogramTitle() {
      return `${this.parameterName} Histogram`
    },
    isMinRangeInvalid() {
      return this.calculateMinRangeInvalid({
        minRange: this.minRange,
        maxRange: this.maxRange,
        lowWarning: this.lowWarning,
        lowCaution: this.lowCaution,
        highCaution: this.highCaution,
        highWarning: this.highWarning,
      })
    },
    isMaxRangeInvalid() {
      return this.calculateMaxRangeInvalid({
        minRange: this.minRange,
        maxRange: this.maxRange,
        lowWarning: this.lowWarning,
        lowCaution: this.lowCaution,
        highCaution: this.highCaution,
        highWarning: this.highWarning,
      })
    },
    isLowWarningInvalid() {
      if (this.lowWarning && isNaN(this.lowWarning)) {
        return true
      } else {
        return this.calculateLowWarningInvalid({
          minRange: this.minRange,
          maxRange: this.maxRange,
          lowWarning: this.lowWarning,
          lowCaution: this.lowCaution,
          highCaution: this.highCaution,
          highWarning: this.highWarning,
        })
      }
    },
    isLowCautionInvalid() {
      if (this.lowCaution && isNaN(this.lowCaution)) {
        return true
      } else {
        return this.calculateLowCautionInvalid({
          minRange: this.minRange,
          maxRange: this.maxRange,
          lowWarning: this.lowWarning,
          lowCaution: this.lowCaution,
          highCaution: this.highCaution,
          highWarning: this.highWarning,
        })
      }
    },
    isHighCautionInvalid() {
      if (this.highCaution && isNaN(this.highCaution)) {
        return true
      } else {
        return this.calculateHighCautionInvalid({
          minRange: this.minRange,
          maxRange: this.maxRange,
          lowWarning: this.lowWarning,
          lowCaution: this.lowCaution,
          highCaution: this.highCaution,
          highWarning: this.highWarning,
        })
      }
    },
    isHighWarningInvalid() {
      if (this.highWarning && isNaN(this.highWarning)) {
        return true
      } else {
        return this.calculateHighWarningInvalid({
          minRange: this.minRange,
          maxRange: this.maxRange,
          lowWarning: this.lowWarning,
          lowCaution: this.lowCaution,
          highCaution: this.highCaution,
          highWarning: this.highWarning,
        })
      }
    },
  },

  mounted() {
    if (
      this.isMinRangeInvalid ||
      this.isMaxRangeInvalid ||
      this.isLowWarningInvalid ||
      this.isLowCautionInvalid ||
      this.isHighCautionInvalid ||
      this.isHighWarningInvalid
    ) {
      this.$emit('invalidThreshold')
    }
  },

  methods: {
    handleUpdateThresholdAlertStatus(value) {
      this.$emit('updateThresholdAlertStatus', value)
    },
    handleUpdateMinRange(value) {
      this.$emit('updateMinRange', {
        ...value,
        valid: !this.isMinRangeInvalid,
      })
    },
    handleUpdateMaxRange(value) {
      this.$emit('updateMaxRange', {
        ...value,
        valid: !this.isMaxRangeInvalid,
      })
    },
    handleUpdateLowWarning(value) {
      this.$emit('updateLowWarning', {
        ...value,
        valid: !this.isLowWarningInvalid,
      })
    },
    handleUpdateLowCaution(value) {
      this.$emit('updateLowCaution', {
        ...value,
        valid: !this.isLowCautionInvalid,
      })
    },
    handleUpdateHighCaution(value) {
      this.$emit('updateHighCaution', {
        ...value,
        valid: !this.isHighCautionInvalid,
      })
    },
    handleUpdateHighWarning(value) {
      this.$emit('updateHighWarning', {
        ...value,
        valid: !this.isHighWarningInvalid,
      })
    },
  },
}
</script>

<style lang="scss">
.asc-customAlerts-histogram-wrapper--container {
  padding: 16px;

  .asc-histogram--title {
    margin-bottom: 20px;
  }

  .asc-customAlerts-histogram-wrapper--chartContainer {
    background-color: $gray-0;
    display: flex;
    min-height: 400px;
    padding: 10px;

    .asc-parameter-graph {
      flex: 3;
      margin-right: 20px;
    }
  }
}
</style>
